import { OverpassElement, Business } from '../types';

const OVERPASS_API_URL = 'https://overpass-api.de/api/interpreter';

// --- Caching Logic ---
// We'll cache the results from the Overpass API to make subsequent loads much faster.
const CACHE_PREFIX = 'xmr_map_cache_';
const CACHE_TTL = 3600 * 1000; // Cache for 1 hour in milliseconds

/**
 * Stores data in localStorage with an expiration time.
 * @param key The cache key.
 * @param data The data to store.
 */
const setCache = (key: string, data: any) => {
    try {
        const item = {
            data: data,
            expiry: Date.now() + CACHE_TTL,
        };
        localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(item));
    } catch (error) {
        console.error("Error setting cache:", error);
    }
};

/**
 * Retrieves data from localStorage if it exists and has not expired.
 * @param key The cache key.
 * @returns The cached data or null.
 */
const getCache = (key: string): any | null => {
    try {
        const itemStr = localStorage.getItem(CACHE_PREFIX + key);
        if (!itemStr) {
            return null;
        }
        const item = JSON.parse(itemStr);
        const now = Date.now();
        // Check if the cache has expired
        if (now > item.expiry) {
            localStorage.removeItem(CACHE_PREFIX + key);
            return null;
        }
        return item.data;
    } catch (error) {
        console.error("Error getting cache:", error);
        return null;
    }
};
// --- End Caching Logic ---


// Rate limiting: minimum time between requests
let lastRequestTime = 0;
const MIN_REQUEST_INTERVAL = 2000; // 2 seconds minimum between requests

// Request queue to prevent concurrent requests
let requestQueue: Promise<any> = Promise.resolve();

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const rateLimit = async () => {
  const now = Date.now();
  const timeSinceLastRequest = now - lastRequestTime;
  
  if (timeSinceLastRequest < MIN_REQUEST_INTERVAL) {
    await delay(MIN_REQUEST_INTERVAL - timeSinceLastRequest);
  }
  
  lastRequestTime = Date.now();
};

// Global Overpass QL query to find ALL businesses accepting XMR worldwide
const buildGlobalOverpassQuery = () => `
[out:json][timeout:60];
(
  // Nodes with XMR payment tags - GLOBAL search
  node["payment:xmr"="yes"];
  node["currency:XMR"="yes"];
  node["payment:cryptocurrency"~"XMR|monero|Monero"];
  node["currency"~"XMR|monero|Monero"];
  node["payment:coins"~"XMR|monero|Monero"];
  
  // Ways (buildings) with XMR payment tags - GLOBAL search
  way["payment:xmr"="yes"];
  way["currency:XMR"="yes"];
  way["payment:cryptocurrency"~"XMR|monero|Monero"];
  way["currency"~"XMR|monero|Monero"];
  way["payment:coins"~"XMR|monero|Monero"];
  
  // Relations with XMR payment tags - GLOBAL search
  relation["payment:xmr"="yes"];
  relation["currency:XMR"="yes"];
  relation["payment:cryptocurrency"~"XMR|monero|Monero"];
  relation["currency"~"XMR|monero|Monero"];
  relation["payment:coins"~"XMR|monero|Monero"];
);
out center meta;
`;

// Regional search for better performance when needed
const buildRegionalOverpassQuery = (bbox: string) => `
[out:json][timeout:30];
(
  // Nodes with XMR payment tags
  node["payment:xmr"="yes"](${bbox});
  node["currency:XMR"="yes"](${bbox});
  node["payment:cryptocurrency"~"XMR|monero|Monero"](${bbox});
  node["currency"~"XMR|monero|Monero"](${bbox});
  node["payment:coins"~"XMR|monero|Monero"](${bbox});
  
  // Ways (buildings) with XMR payment tags
  way["payment:xmr"="yes"](${bbox});
  way["currency:XMR"="yes"](${bbox});
  way["payment:cryptocurrency"~"XMR|monero|Monero"](${bbox});
  way["currency"~"XMR|monero|Monero"](${bbox});
  way["payment:coins"~"XMR|monero|Monero"](${bbox});
  
  // Relations with XMR payment tags
  relation["payment:xmr"="yes"](${bbox});
  relation["currency:XMR"="yes"](${bbox});
  relation["payment:cryptocurrency"~"XMR|monero|Monero"](${bbox});
  relation["currency"~"XMR|monero|Monero"](${bbox});
  relation["payment:coins"~"XMR|monero|Monero"](${bbox});
);
out center meta;
`;

// Duplicate detection based on name and location proximity
const removeDuplicates = (businesses: Business[]): Business[] => {
  const uniqueBusinesses: Business[] = [];
  const seen = new Set<string>();

  for (const business of businesses) {
    // Create a key based on normalized name and approximate location
    const normalizedName = business.name.toLowerCase().trim();
    const roundedLat = Math.round(business.coordinates.lat * 1000) / 1000; // ~100m precision
    const roundedLng = Math.round(business.coordinates.lng * 1000) / 1000;
    const key = `${normalizedName}_${roundedLat}_${roundedLng}`;

    if (!seen.has(key)) {
      seen.add(key);
      uniqueBusinesses.push(business);
    } else {
      console.log(`Duplicate filtered: ${business.name} at ${business.coordinates.lat}, ${business.coordinates.lng}`);
    }
  }

  console.log(`Filtered ${businesses.length - uniqueBusinesses.length} duplicates, ${uniqueBusinesses.length} unique businesses remain`);
  return uniqueBusinesses;
};

export const fetchAllXMRBusinesses = async (): Promise<Business[]> => {
    const cacheKey = 'all_businesses';
    const cachedData = getCache(cacheKey);
    if (cachedData) {
        console.log('Returning cached XMR businesses.');
        return cachedData;
    }

  // Queue this request to prevent concurrent API calls
  return requestQueue = requestQueue.then(async () => {
    await rateLimit();

    try {
      console.log('Fetching ALL XMR businesses globally from API...');
      
      const query = buildGlobalOverpassQuery();
      
      const response = await fetch(OVERPASS_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: `data=${encodeURIComponent(query)}`,
      });

      if (!response.ok) {
        throw new Error(`Overpass API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Global Overpass API Response:', data);
      
      if (!data.elements || data.elements.length === 0) {
        console.log('No XMR businesses found globally in OSM');
        return [];
      }
      
      const realBusinesses = parseOverpassData(data.elements);
      console.log(`Found ${realBusinesses.length} real XMR businesses globally`);
            
      const uniqueBusinesses = removeDuplicates(realBusinesses);
      console.log(`Total unique businesses: ${uniqueBusinesses.length}`);
      
      // Store the fresh data in the cache
      setCache(cacheKey, uniqueBusinesses);

      return uniqueBusinesses;
      
    } catch (error) {
      console.error('Error fetching global XMR businesses:', error);
      return [];
    }
  });
};

export const fetchXMRBusinesses = async (
  south: number,
  west: number,
  north: number,
  east: number
): Promise<Business[]> => {
  // This function is for regional searches and won't use the main cache for now,
  // as the viewport changes constantly. The main performance gain is on the initial global fetch.
  return requestQueue = requestQueue.then(async () => {
    await rateLimit();

    try {
      const bbox = `${south},${west},${north},${east}`;
      const query = buildRegionalOverpassQuery(bbox);
      
      console.log('Regional Overpass Query:', query);
      console.log('Bounding Box:', bbox);
      
      const response = await fetch(OVERPASS_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: `data=${encodeURIComponent(query)}`,
      });

      if (!response.ok) {
        throw new Error(`Overpass API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Regional Overpass API Response:', data);
      
      if (!data.elements || data.elements.length === 0) {
        console.log('No XMR businesses found in this region');
        return [];
      }
      
      const realBusinesses = parseOverpassData(data.elements);
      
      return removeDuplicates(realBusinesses);
    } catch (error) {
      console.error('Error fetching regional XMR businesses:', error);
      return [];
    }
  });
};

// IMPORTANT: This function does NOT add to Overpass API (which is read-only)
// It stores businesses locally and they will appear in our app immediately
// To actually add to OpenStreetMap (and eventually Overpass), use the OSM API
export const addBusiness = async (business: Omit<Business, 'id'>): Promise<Business> => {
  const newBusiness: Business = {
    ...business,
    id: `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
  };

  // Store in localStorage for immediate display in our app
  const existingBusinesses = getStoredBusinesses();
  const updatedBusinesses = [...existingBusinesses, newBusiness];
  localStorage.setItem('userAddedBusinesses', JSON.stringify(updatedBusinesses));

  console.log('Business added to local storage (not OSM yet):', newBusiness);
  
  return newBusiness;
};

// Get user-added businesses from localStorage
export const getStoredBusinesses = (): Business[] => {
  try {
    const stored = localStorage.getItem('userAddedBusinesses');
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error loading stored businesses:', error);
    return [];
  }
};

const parseOverpassData = (elements: OverpassElement[]): Business[] => {
  console.log(`Parsing ${elements.length} OSM elements`);
  
  return elements.map((element) => {
    const tags = element.tags || {};
    
    // Get coordinates
    let lat: number, lng: number;
    if (element.type === 'node' && element.lat && element.lon) {
      lat = element.lat;
      lng = element.lon;
    } else if (element.center) {
      lat = element.center.lat;
      lng = element.center.lon;
    } else {
      console.warn(`Element ${element.id} has no coordinates`);
      return null;
    }

    // Extract payment methods
    const xmrPaymentMethods = extractPaymentMethods(tags);
    
    // Determine category from OSM tags
    const category = determineCategory(tags);
    
    // Build address from OSM tags
    const address = buildAddress(tags);
    
    // Build name with fallbacks
    const name = tags.name || tags.brand || tags.operator || `${category} (OSM ID: ${element.id})`;
    
    const business: Business = {
      id: `osm_${element.type}_${element.id}`,
      name,
      category,
      description: tags.description || buildDescription(tags, category),
      address,
      coordinates: { lat, lng },
      website: tags.website || tags['contact:website'] || tags.url,
      phone: tags.phone || tags['contact:phone'],
      email: tags.email || tags['contact:email'],
      xmrPaymentMethods,
      isOnline: true,
      rating: undefined,
      reviews: undefined,
      openHours: tags.opening_hours || 'Hours not specified',
      osmId: element.id.toString(),
      osmType: element.type,
      tags
    };
    
    return business;
  }).filter((business): business is Business => business !== null);
};

const extractPaymentMethods = (tags: Record<string, string>): string[] => {
  const methods: string[] = [];
  
  if (tags['payment:xmr'] === 'yes') {
    methods.push('XMR Direct Payment');
  }
  
  if (tags['currency:XMR'] === 'yes') {
    methods.push('XMR Currency');
  }
  
  if (tags['payment:cryptocurrency'] && tags['payment:cryptocurrency'].toLowerCase().includes('xmr')) {
    methods.push('Cryptocurrency Wallet');
  }
  
  if (tags.currency && tags.currency.toLowerCase().includes('xmr')) {
    methods.push('XMR Accepted');
  }
  
  if (tags['payment:contactless'] === 'yes') {
    methods.push('Contactless Payment');
  }
  
  if (methods.length === 0) {
    methods.push('XMR Accepted');
  }
  
  return methods;
};

const determineCategory = (tags: Record<string, string>): string => {
  const amenity = tags.amenity;
  const shop = tags.shop;
  const tourism = tags.tourism;
  const craft = tags.craft;
  const office = tags.office;
  
  if (amenity) {
    switch (amenity) {
      case 'restaurant':
      case 'cafe':
      case 'bar':
      case 'pub':
      case 'fast_food':
      case 'food_court':
      case 'biergarten':
        return 'restaurant';
      case 'bank':
      case 'atm':
      case 'bureau_de_change':
      case 'money_transfer':
        return 'service';
      case 'hotel':
        return 'hotel';
      case 'cinema':
      case 'theatre':
      case 'nightclub':
      case 'casino':
        return 'entertainment';
      default:
        return 'service';
    }
  }
  
  if (shop) {
    switch (shop) {
      case 'computer':
      case 'electronics':
      case 'mobile_phone':
      case 'software':
        return 'tech';
      case 'clothes':
      case 'shoes':
      case 'jewelry':
      case 'books':
      case 'gift':
      case 'art':
      case 'music':
        return 'retail';
      default:
        return 'retail';
    }
  }
  
  if (tourism) {
    switch (tourism) {
      case 'hotel':
      case 'hostel':
      case 'guest_house':
      case 'motel':
        return 'hotel';
      case 'attraction':
      case 'museum':
      case 'gallery':
        return 'entertainment';
      default:
        return 'service';
    }
  }
  
  if (craft || office) {
    return 'service';
  }
  
  return 'service';
};

const buildAddress = (tags: Record<string, string>): string => {
  const parts: string[] = [];
  
  if (tags['addr:housenumber'] && tags['addr:street']) {
    parts.push(`${tags['addr:housenumber']} ${tags['addr:street']}`);
  } else if (tags['addr:street']) {
    parts.push(tags['addr:street']);
  }
  
  if (tags['addr:city']) {
    parts.push(tags['addr:city']);
  }
  
  if (tags['addr:state'] || tags['addr:province']) {
    parts.push(tags['addr:state'] || tags['addr:province']);
  }
  
  if (tags['addr:postcode']) {
    parts.push(tags['addr:postcode']);
  }
  
  if (tags['addr:country']) {
    parts.push(tags['addr:country']);
  }
  
  return parts.length > 0 ? parts.join(', ') : 'Address not specified';
};

const buildDescription = (tags: Record<string, string>, category: string): string => {
  const amenity = tags.amenity;
  const shop = tags.shop;
  const brand = tags.brand;
  const cuisine = tags.cuisine;
  
  let description = '';
  
  if (brand) {
    description += `${brand} `;
  }
  
  if (amenity) {
    description += `${amenity.replace(/_/g, ' ')} `;
  } else if (shop) {
    description += `${shop.replace(/_/g, ' ')} shop `;
  }
  
  if (cuisine) {
    description += `serving ${cuisine.replace(/_/g, ' ')} cuisine `;
  }
  
  description += 'accepting Monero (XMR) payments';
  
  return description.trim();
};
